/** @jsx jsx */
import React from 'react';

import { css as cn, cx } from '@emotion/css';
import { css, jsx, keyframes } from '@emotion/react';

import { bricks, HTMLBricksProps } from '../../system';
import { colors } from '../../theme/eero';
import { BREAKPOINTS_DICT, media } from '../../theme/legacy/foundations/mediaBreakpoints';
import Box, { BoxProps } from '../Box';

export interface IProps {
  padding?: 'none' | 'less' | 'extra';
  shade?: 'light' | 'medium' | 'dark' | 'blue';
  size?: number;
  borderWidth?: number;
}

const Section: React.FC<HTMLBricksProps<'section'> & Pick<IProps, 'padding'>> = ({
  padding,
  ...props
}) => (
  <bricks.section
    maxWidth={`${BREAKPOINTS_DICT.XL}px`}
    mx="auto"
    textAlign="center"
    py={padding === 'none' ? 0 : '2rem'}
    {...props}
  />
);

const extraPadding = css`
  padding-top: 6rem;
  padding-bottom: 5rem;

  ${media.smallerThan.SM} {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }
`;

const lessPadding = css`
  padding-top: 1rem;
  padding-bottom: 1rem;
`;

const noPadding = css`
  padding-top: 0;
  padding-bottom: 0;
`;

const inProgressRotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoaderWrapper = (props) => (
  <Box
    display="inline-block"
    position="relative"
    width="18px"
    height="18px"
    sx={{
      '& div:nth-of-type(1)': {
        animationDelay: '-0.45s',
      },
      '& div:nth-of-type(2)': {
        animationDelay: '-0.3s',
      },
      '& div:nth-of-type(3)': {
        animationDelay: '-0.15s',
      },
    }}
    {...props}
  />
);

const Spinner: React.FC<BoxProps & { size?: number; borderWidth?: number }> = ({
  size,
  borderWidth,
  ...props
}) => (
  <Box
    boxSizing="border-box"
    position="absolute"
    width={`${size ?? 24}px`}
    height={`${size ?? 24}px`}
    top="50%"
    left="50%"
    marginTop={`-${size ? size / 2 : 12}px`}
    marginLeft={`-${size ? size / 2 : 12}px`}
    borderWidth={`${borderWidth ?? 3}px`}
    borderStyle="solid"
    borderRadius="full"
    animation={` 0.6s linear 0s infinite normal none running ${inProgressRotate}`}
    {...props}
  />
);

const light = css`
  border-color: ${colors.neutrals0} transparent transparent transparent;
`;

const medium = css`
  border-color: ${colors.neutrals30} transparent transparent transparent;
`;

const dark = css`
  border-color: ${colors.neutrals100} transparent transparent transparent;
`;

const blue = css`
  border-color: ${colors.brand50} transparent transparent transparent;
`;

const Loader: React.FC<IProps> = ({ padding, shade, ...props }) => {
  const sectionPadding = cx(
    { [cn(extraPadding)]: padding === 'extra' },
    { [cn(lessPadding)]: padding === 'less' },
    { [cn(noPadding)]: padding === 'none' },
  );

  const loaderColor = cx(
    { [cn(blue)]: shade === 'blue' },
    { [cn(light)]: shade === 'light' },
    { [cn(dark)]: shade === 'dark' || shade === undefined },
    { [cn(medium)]: shade === 'medium' },
  );

  return (
    <Section className={sectionPadding} padding={padding}>
      <LoaderWrapper>
        <Spinner {...props} className={loaderColor} />
        <Spinner {...props} className={loaderColor} />
        <Spinner {...props} className={loaderColor} />
      </LoaderWrapper>
    </Section>
  );
};
Loader.displayName = 'Loader';

export default Loader;
